import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  Mesh,
  type Material,
} from 'three';
import type {
  DoorConfig,
  FacadeSide,
  HouseBodyConfig,
  PartitionDoorConfig,
  StoryConfig,
  TerraceConfig,
  WindowConfig,
} from '@/model/schema';
import type { MaterialKit } from '../materials';
import type { HingeDef } from '../hinges';
import { terraceFacadeCuts } from '@/generator/extrasGenerator';
import { bakeFrameWorldUVs } from '../uv';

export interface FacadeDecor {
  /** All-or-none mullion crosses on every glass pane. */
  windowMullion: boolean;
}

/**
 * Sink for hinge defs collected during a build pass. Builders push every
 * door / window leaf they wrap in a pivot group; the App then forwards the
 * accumulated list into `useHingesStore` so the UI panel can render
 * sliders for them.
 */
export interface HingeCollector {
  add(def: HingeDef): void;
}

/**
 * Wall segmentation for openings.
 *
 * Design doc §"Geometry Generation Approach" explicitly recommends wall
 * segmentation over CSG: openings are created by splitting each facade into
 * left / right / top / bottom rectangles around each window and door. CSG
 * would give cleaner geometry but is numerically unstable and produces
 * worse export topology.
 *
 * For each facade we:
 *   1. Collect opening spans (windows and, on ground floor only, doors).
 *   2. Sort and merge overlapping spans.
 *   3. For each span build left pillar (if any), top lintel, bottom sill
 *      (for windows only — doors go to the floor), and advance.
 *   4. After the last span, build a final pillar to the far corner.
 */

export interface StoryWallsOutput {
  group: Group;
}

/**
 * Facade-local frame used for authoring wall strips. Each facade is built in
 * its own right-handed frame where:
 *   local +X runs along the facade from the "origin corner" toward the other
 *   local +Y is world up
 *   local +Z points INTO the house (i.e. the inward normal)
 * We then rotate the strip into world space. The table below is written out
 * per side rather than derived because the rotation signs are easier to
 * audit this way.
 */
interface FacadeDims {
  side: FacadeSide;
  length: number;
}

function facadeDims(body: HouseBodyConfig): Record<FacadeSide, FacadeDims> {
  const w = body.footprintWidth;
  const d = body.footprintDepth;
  return {
    front: { side: 'front', length: w },
    back: { side: 'back', length: w },
    left: { side: 'left', length: d },
    right: { side: 'right', length: d },
  };
}

interface OpeningSpan {
  lo: number;
  hi: number;
  /** Opening bottom (sill) and top, measured from the story floor. */
  bottom: number;
  top: number;
  kind: 'window' | 'door';
  /** Original object for per-opening mesh placement (window glass pane, door slab). */
  window?: WindowConfig;
  door?: DoorConfig;
}

function openingsForSide(
  story: StoryConfig,
  side: FacadeSide,
): OpeningSpan[] {
  const spans: OpeningSpan[] = [];
  for (const w of story.windows) {
    if (w.side !== side) continue;
    spans.push({
      lo: w.offset,
      hi: w.offset + w.width,
      bottom: w.sillHeight,
      top: w.sillHeight + w.height,
      kind: 'window',
      window: w,
    });
  }
  for (const d of story.doors) {
    if (d.side !== side) continue;
    if (
      d.role !== 'front' &&
      d.role !== 'rear' &&
      d.role !== 'side' &&
      d.role !== 'garage'
    )
      continue;
    spans.push({
      lo: d.offset,
      hi: d.offset + d.width,
      bottom: 0,
      top: d.height,
      kind: 'door',
      door: d,
    });
  }
  spans.sort((a, b) => a.lo - b.lo);
  return spans;
}

/**
 * Build one facade's segmented wall strip as a group of boxes. The strip is
 * authored in local 2D "u along facade, y up" space, then we rotate/translate
 * the group into place.
 */
/** Build a pivot group anchored at (uPivot, 0, wallThickness/2) in
 *  facade-local coords. The group's name is `Hinge_<id>` so the viewport
 *  can later locate it; the def is stashed on userData for export. */
function makeHingeGroup(
  uPivot: number,
  wallThickness: number,
  def: HingeDef,
): Group {
  const g = new Group();
  g.name = `Hinge_${def.id}`;
  g.userData.hinge = def;
  g.position.set(uPivot, 0, wallThickness / 2);
  return g;
}

/**
 * Terrace notches remove an entire floor-to-ceiling SEGMENT from the
 * host facade at their `level` and at every level above (since the
 * notch cuts through every story from deck up). We model that as
 * per-facade skip intervals: the wall builder never emits pillars,
 * sills, or lintels inside a skip span, and the cursor jumps past it.
 */
function facadeSkipSpansForTerraces(
  body: HouseBodyConfig,
  terraces: TerraceConfig[],
  storyLevel: number,
  side: FacadeSide,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  for (const t of terraces) {
    if (storyLevel < t.level) continue;
    // Corner terraces cut TWO facades: the host (`t.side`) and an
    // adjacent neighbour determined by (side, corner). We ask the
    // generator's shared helper where the cuts go so both sides stay
    // in lockstep with the AABB used by the slab-hole and roof-cut
    // codepaths.
    const cuts = terraceFacadeCuts(body, t);
    if (cuts.hostSide === side) out.push([cuts.hostLo, cuts.hostHi]);
    if (cuts.adjSide === side) out.push([cuts.adjLo, cuts.adjHi]);
  }
  out.sort((a, b) => a[0] - b[0]);
  return out;
}

function buildFacade(
  body: HouseBodyConfig,
  story: StoryConfig,
  dims: FacadeDims,
  kit: MaterialKit,
  decor: FacadeDecor,
  hinges: HingeCollector,
  terraces: TerraceConfig[] = [],
): Group {
  const group = new Group();
  group.name = `Wall_${dims.side}`;
  const spans = openingsForSide(story, dims.side);

  const wallThickness = body.exteriorWallThickness;
  const storyHeight = body.storyHeight;

  // Bucketed span indices so each emitted mesh gets a unique sibling name.
  // Generic labels like "pillar" / "sill" are unhelpful in USDA — every
  // segment becomes `Pillar_000`, `Sill_001`, ... instead.
  const counts = { pillar: 0, sill: 0, lintel: 0 };
  const addSlab = (
    uStart: number,
    uEnd: number,
    yStart: number,
    yEnd: number,
    kind: 'pillar' | 'sill' | 'lintel',
  ) => {
    const width = Math.max(0, uEnd - uStart);
    const height = Math.max(0, yEnd - yStart);
    if (width < 1e-3 || height < 1e-3) return;
    const geo = new BoxGeometry(width, height, wallThickness);
    const mesh = new Mesh(geo, kit.exteriorWall);
    const cap = kind[0]!.toUpperCase() + kind.slice(1);
    mesh.name = `${cap}_${counts[kind].toString().padStart(3, '0')}`;
    counts[kind] += 1;
    mesh.userData.ownsGeometry = true;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    // Position in facade-local space: u along +X, y up, thickness along +Z
    // (local +Z points inward, so the wall occupies z in [0, thickness]).
    const mx = uStart + width / 2;
    const my = yStart + height / 2;
    const mz = wallThickness / 2;
    mesh.position.set(mx, my, mz);
    // Bake facade-local UVs so every pillar / sill / lintel on this
    // side shares one continuous brick grid — see uv.ts for the
    // reasoning.
    bakeFrameWorldUVs(geo, mx, my, mz);
    group.add(mesh);
  };

  const skips = facadeSkipSpansForTerraces(body, terraces, story.level, dims.side);
  // Walk the facade in "run" chunks, where each run is a contiguous
  // stretch of wall between two skip spans (or between a lot corner and
  // a skip). Within a run we keep the exact same pillar / sill / lintel
  // emission logic as before. Skip spans contribute NO geometry to the
  // host facade — the terrace opens to outside there.
  const runBounds: Array<[number, number]> = [];
  {
    let lo = 0;
    for (const [sLo, sHi] of skips) {
      const cLo = Math.max(0, Math.min(dims.length, sLo));
      const cHi = Math.max(0, Math.min(dims.length, sHi));
      if (cHi <= cLo) continue;
      if (cLo > lo) runBounds.push([lo, cLo]);
      lo = Math.max(lo, cHi);
    }
    if (lo < dims.length) runBounds.push([lo, dims.length]);
  }
  if (runBounds.length === 0) {
    // The entire facade is eaten by terrace skips. Skip all geometry —
    // the terrace builder will close this span with its own interior
    // wall. Still run through the switch-block below for group
    // positioning.
  }

  for (const [runLo, runHi] of runBounds) {
    emitFacadeRun(runLo, runHi);
  }

  function emitFacadeRun(runLo: number, runHi: number) {
    const runSpans = spans.filter((s) => s.lo >= runLo && s.hi <= runHi);
    let cursor = runLo;
    for (const span of runSpans) {
      if (span.lo > cursor) addSlab(cursor, span.lo, 0, storyHeight, 'pillar');
      if (span.bottom > 0) addSlab(span.lo, span.hi, 0, span.bottom, 'sill');
      if (span.top < storyHeight) addSlab(span.lo, span.hi, span.top, storyHeight, 'lintel');
      fillOpening(span);
      cursor = Math.max(cursor, span.hi);
    }
    if (cursor < runHi) addSlab(cursor, runHi, 0, storyHeight, 'pillar');
  }

  function fillOpening(span: OpeningSpan) {

    // Opening fill: glass pane + frame (window), or door slab + frame + knob
    // (exterior door). All centered on the wall thickness midplane so they
    // read correctly from both sides. The pane is sized flush to the
    // opening minus a 2mm edge so we don't z-fight the neighbouring slabs.
    const paneEdge = 0.002;
    const frameThickness = 0.06;
    const frameDepth = 0.08;
    if (span.kind === 'window' && span.window) {
      const paneW = Math.max(0.05, span.hi - span.lo - paneEdge);
      const paneH = Math.max(0.05, span.top - span.bottom - paneEdge);
      const cx = span.lo + (span.hi - span.lo) / 2;
      const cy = span.bottom + (span.top - span.bottom) / 2;

      const innerW = Math.max(0.05, paneW - 2 * frameThickness);
      const innerH = Math.max(0.05, paneH - 2 * frameThickness);

      // Wrap the whole opening in a named container so the exported
      // hierarchy reads as `Window_<id> { Frame, Hinge { Sash, Pane } }`.
      // The container sits at facade-local origin, so its children use
      // the same coordinates they would inside the bare facade group.
      const windowGroup = new Group();
      windowGroup.name = `Window_${span.window.id}`;
      group.add(windowGroup);

      // The OUTER trim frame is part of the masonry — it stays put when
      // the casement swings open. (See addSashFrame below for the moving
      // sash that hugs the glass.) Pass the container as parent so the
      // frame lands under `Window_<id>/Frame_*`.
      addFrame(windowGroup, kit.windowFrame, cx, cy, paneW, paneH, frameThickness, frameDepth, wallThickness, 'WindowFrame');

      // Casement window: hinged on one vertical edge, swings outward.
      // The leaf is positioned at pivot-local +X. In facade-local coords
      // +Z is INWARD, and a POSITIVE Y rotation maps +X → -Z, i.e.
      // pushes the leaf OUTWARD. So the open angle is +deg and the open
      // limit is at maxDeg. (Casement windows typically swing 60° max.)
      const hingeU = cx - innerW / 2;
      const def: HingeDef = {
        id: `win-L${story.level}-${dims.side}-${span.window.id}`,
        label: `Window L${story.level} · ${dims.side} · ${formatOffset(span.window.offset)}m`,
        category: 'window',
        axis: 'Y',
        minDeg: 0,
        maxDeg: 60,
        openSignFromMin: 0, // closed = 0deg (min), open = +60deg (max)
      };
      hinges.add(def);
      const pivot = makeHingeGroup(hingeU, wallThickness, def);

      // Sash: a wooden frame that hugs the four edges of the glass. The
      // sash + glass together form the moveable LEAF, so they must be
      // children of the pivot and swing as one. Without this, opening
      // the window leaves a glass rectangle floating with no border —
      // user-reported in image 2.
      const sashThickness = 0.045;
      const sashDepth = 0.04;
      addSashFrame(pivot, kit.windowFrame, innerW, innerH, cy, sashThickness, sashDepth);

      const glassW = Math.max(0.05, innerW - 2 * sashThickness);
      const glassH = Math.max(0.05, innerH - 2 * sashThickness);
      const paneGeo = new BoxGeometry(glassW, glassH, 0.02);
      const pane = new Mesh(paneGeo, kit.window);
      pane.name = `Pane`;
      pane.position.set(innerW / 2, cy, 0);
      pane.castShadow = false;
      pane.receiveShadow = true;
      pivot.add(pane);

      if (decor.windowMullion) {
        const vGeo = new BoxGeometry(0.025, glassH, sashDepth);
        const vBar = new Mesh(vGeo, kit.windowFrame);
        vBar.name = 'Mullion_vertical';
        vBar.position.set(innerW / 2, cy, 0);
        vBar.castShadow = true;
        vBar.receiveShadow = true;
        pivot.add(vBar);
        const hGeo = new BoxGeometry(glassW, 0.025, sashDepth);
        const hBar = new Mesh(hGeo, kit.windowFrame);
        hBar.name = 'Mullion_horizontal';
        hBar.position.set(innerW / 2, cy, 0);
        hBar.castShadow = true;
        hBar.receiveShadow = true;
        pivot.add(hBar);
      }
      windowGroup.add(pivot);
    } else if (span.kind === 'door' && span.door) {
      const slabW = Math.max(0.2, span.hi - span.lo - paneEdge);
      const slabH = Math.max(0.5, span.top - paneEdge);
      const cx = span.lo + (span.hi - span.lo) / 2;

      const doorGroup = new Group();
      doorGroup.name = `Door_${span.door.id}`;
      group.add(doorGroup);

      addFrame(doorGroup, kit.doorFrame, cx, slabH / 2, slabW, slabH, frameThickness, frameDepth, wallThickness, 'DoorFrame');

      // Hinge on the LEFT edge (lower facade-u). The leaf sits at pivot
      // -local +X. To make a residential door swing INWARD (toward
      // facade-local +Z, which is the interior), we need NEGATIVE Y
      // rotation: positive Y rotates +X → -Z (outward). So the open
      // limit is at minDeg = -100 and "closed" is 0.
      const innerW = slabW - 2 * frameThickness;
      const innerH = slabH - frameThickness;
      const hingeU = cx - innerW / 2;
      const def: HingeDef = {
        id: `door-L${story.level}-${dims.side}-${span.door.id}`,
        label: doorLabel(span.door, dims.side, story.level),
        category: 'exterior-door',
        axis: 'Y',
        minDeg: -100,
        maxDeg: 0,
        openSignFromMin: 1, // closed = 0deg (max), open = -100deg (min)
      };
      hinges.add(def);
      const pivot = makeHingeGroup(hingeU, wallThickness, def);

      const doorGeo = new BoxGeometry(innerW, innerH, 0.04);
      const door = new Mesh(doorGeo, kit.door);
      door.name = 'Leaf';
      door.position.set(innerW / 2, innerH / 2, 0);
      door.castShadow = true;
      door.receiveShadow = true;
      pivot.add(door);

      // Knob on the latch side (opposite the hinge). Local +X is the
      // "free" edge of the door now that the pivot sits at u=0. We
      // emit TWO knobs — one on each face — because real exterior
      // doors have a knob/handle on the inside too, and the user
      // reported the missing inside knob.
      const knobX = innerW - 0.07;
      const knobY = 1.05;
      const knobGeo = new CylinderGeometry(0.03, 0.03, 0.05, 12);
      for (const sideZ of [0.04, -0.04]) {
        const knob = new Mesh(knobGeo, kit.doorKnob);
        knob.name = sideZ > 0 ? 'Knob_outside' : 'Knob_inside';
        knob.rotation.x = Math.PI / 2;
        knob.position.set(knobX, knobY, sideZ);
        knob.castShadow = true;
        knob.receiveShadow = true;
        pivot.add(knob);
      }
      doorGroup.add(pivot);
    }
  }

  // Map each facade's local frame (+X along facade, +Z inward) into world
  // coordinates. The `origin corner` is the one from which u=0 starts.
  switch (dims.side) {
    case 'front':
      // origin = front-left; u→+X; inward→+Z
      group.position.set(-body.footprintWidth / 2, 0, -body.footprintDepth / 2);
      break;
    case 'right':
      // origin = front-right; u→+Z (front to back); inward→-X
      group.position.set(body.footprintWidth / 2, 0, -body.footprintDepth / 2);
      group.rotation.y = -Math.PI / 2;
      break;
    case 'back':
      // origin = back-right; u→-X; inward→-Z
      group.position.set(body.footprintWidth / 2, 0, body.footprintDepth / 2);
      group.rotation.y = Math.PI;
      break;
    case 'left':
      // origin = back-left; u→-Z (back to front); inward→+X
      group.position.set(-body.footprintWidth / 2, 0, body.footprintDepth / 2);
      group.rotation.y = Math.PI / 2;
      break;
  }
  return group;
}

export function buildStoryWalls(
  body: HouseBodyConfig,
  story: StoryConfig,
  kit: MaterialKit,
  decor: FacadeDecor,
  hinges: HingeCollector,
  terraces: TerraceConfig[] = [],
): Group {
  const dims = facadeDims(body);
  const group = new Group();
  group.name = 'ExteriorWalls';

  (['front', 'back', 'left', 'right'] as FacadeSide[]).forEach((side) => {
    group.add(buildFacade(body, story, dims[side], kit, decor, hinges, terraces));
  });

  return group;
}

function formatOffset(n: number): string {
  return n.toFixed(1);
}

function doorLabel(door: DoorConfig, side: FacadeSide, level: number): string {
  if (door.role === 'front') return 'Front Door';
  if (door.role === 'rear') return 'Back Door';
  if (door.role === 'garage') return 'Garage Service Door';
  const sideTitle = side[0]!.toUpperCase() + side.slice(1);
  return `${sideTitle} Door · L${level}`;
}

/**
 * Interior door jamb: a thin trim hugging the top + two sides of the
 * partition opening. Authored in body-local coords (matches the
 * partition slab() helper). Added to the partitions group so it does
 * NOT swing with the door slab.
 */
function addInteriorDoorJamb(
  parent: Group,
  kit: MaterialKit,
  body: HouseBodyConfig,
  xc: number,
  zc: number,
  doorWidth: number,
  doorHeight: number,
  thickness: number,
  depth: number,
  axis: 'X' | 'Z',
): void {
  const halfW = doorWidth / 2;
  const innerH = doorHeight - thickness;
  const yHead = innerH + thickness / 2;

  const place = (sx: number, sy: number, sz: number, x: number, y: number, z: number, name: string) => {
    const mesh = new Mesh(new BoxGeometry(sx, sy, sz), kit.doorFrame);
    mesh.name = name;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.position.set(
      -body.footprintWidth / 2 + x,
      y,
      -body.footprintDepth / 2 + z,
    );
    parent.add(mesh);
  };

  if (axis === 'X') {
    // Vertical wall along Z at x = xc. Jamb spans Z, depth runs along X.
    place(depth, thickness, doorWidth, xc, yHead, zc, 'Jamb_top');
    place(depth, innerH, thickness, xc, innerH / 2, zc - halfW + thickness / 2, 'Jamb_side_a');
    place(depth, innerH, thickness, xc, innerH / 2, zc + halfW - thickness / 2, 'Jamb_side_b');
  } else {
    // Horizontal wall along X at z = zc. Jamb spans X, depth runs along Z.
    place(doorWidth, thickness, depth, xc, yHead, zc, 'Jamb_top');
    place(thickness, innerH, depth, xc - halfW + thickness / 2, innerH / 2, zc, 'Jamb_side_a');
    place(thickness, innerH, depth, xc + halfW - thickness / 2, innerH / 2, zc, 'Jamb_side_b');
  }
}

/**
 * Sash frame: 4 thin bars hugging the four edges of the casement glass.
 * Authored in pivot-local coords where the leaf extends from x=0 to x=innerW
 * (the latch side), centred on cy in y, and on z=0 (wall midplane). Because
 * the bars are added to the SAME pivot as the glass, they all swing
 * together when the user opens the hinge.
 */
function addSashFrame(
  pivot: Group,
  material: Material,
  innerW: number,
  innerH: number,
  cy: number,
  thickness: number,
  depth: number,
): void {
  const halfW = innerW / 2;
  const halfH = innerH / 2;
  const cxLocal = halfW; // pivot is at x=0, leaf extends to +innerW
  const bars: Array<{ s: [number, number, number]; p: [number, number, number]; name: string }> = [
    {
      s: [innerW, thickness, depth],
      p: [cxLocal, cy + halfH - thickness / 2, 0],
      name: 'Sash_top',
    },
    {
      s: [innerW, thickness, depth],
      p: [cxLocal, cy - halfH + thickness / 2, 0],
      name: 'Sash_bottom',
    },
    {
      s: [thickness, innerH - 2 * thickness, depth],
      p: [thickness / 2, cy, 0],
      name: 'Sash_hingeSide',
    },
    {
      s: [thickness, innerH - 2 * thickness, depth],
      p: [innerW - thickness / 2, cy, 0],
      name: 'Sash_latchSide',
    },
  ];
  for (const { s, p, name } of bars) {
    const bar = new Mesh(new BoxGeometry(s[0], s[1], s[2]), material);
    bar.name = name;
    bar.position.set(p[0], p[1], p[2]);
    bar.castShadow = true;
    bar.receiveShadow = true;
    pivot.add(bar);
  }
}

/**
 * Adds a 4-piece rectangular trim frame around an opening: top, bottom,
 * left, right. The whole frame straddles the wall thickness so it reads
 * from both sides; depth is exterior-facing.
 *
 * `labelPrefix` controls how the emitted bars are named so USDA export
 * reads naturally (WindowFrame_top, DoorFrame_left, ...).
 */
function addFrame(
  parent: Group,
  material: Material,
  cx: number,
  cy: number,
  outerW: number,
  outerH: number,
  thickness: number,
  depth: number,
  wallThickness: number,
  labelPrefix = 'Frame',
) {
  const halfW = outerW / 2;
  const halfH = outerH / 2;
  const bars: Array<{ geo: BoxGeometry; pos: [number, number, number]; name: string }> = [
    {
      geo: new BoxGeometry(outerW, thickness, depth),
      pos: [cx, cy + halfH - thickness / 2, wallThickness / 2],
      name: `${labelPrefix}_top`,
    },
    {
      geo: new BoxGeometry(outerW, thickness, depth),
      pos: [cx, cy - halfH + thickness / 2, wallThickness / 2],
      name: `${labelPrefix}_bottom`,
    },
    {
      geo: new BoxGeometry(thickness, outerH - 2 * thickness, depth),
      pos: [cx - halfW + thickness / 2, cy, wallThickness / 2],
      name: `${labelPrefix}_left`,
    },
    {
      geo: new BoxGeometry(thickness, outerH - 2 * thickness, depth),
      pos: [cx + halfW - thickness / 2, cy, wallThickness / 2],
      name: `${labelPrefix}_right`,
    },
  ];
  for (const { geo, pos, name } of bars) {
    const bar = new Mesh(geo, material);
    bar.name = name;
    bar.position.set(pos[0], pos[1], pos[2]);
    bar.castShadow = true;
    bar.receiveShadow = true;
    parent.add(bar);
  }
}

/**
 * Interior partition walls as thin slabs from the room rectangles.
 *
 * Each room owns its +X and +Z walls; the -X / -Z sides are covered by
 * neighbouring rooms' +X / +Z walls, so every internal edge is drawn exactly
 * once (no doubling).
 *
 * Doorway selection
 * -----------------
 * Unlike the v1 renderer (which placed a centred door in EVERY long enough
 * partition), v2 consults `story.partitionDoors`: the room-graph planner
 * picks a spanning tree of doors so every room is reachable, but most
 * walls remain solid. A visible interior door slab is rendered in each
 * selected opening.
 *
 * Partitions that coincide with the exterior wall are skipped (the
 * exterior already fills that edge), and partitions are clamped to the
 * interior rectangle so they don't poke through the exterior walls.
 */
export function buildInteriorPartitions(
  body: HouseBodyConfig,
  story: StoryConfig,
  kit: MaterialKit,
  hinges: HingeCollector,
): Group {
  const group = new Group();
  group.name = 'InteriorWalls';
  // Typical interior partition: 2×4 stud wall with drywall both sides ≈
  // 10 cm. The old 8 cm value read as too thin on close inspection.
  const partitionThickness = 0.1;
  const partitionHeight = body.storyHeight * 0.98;

  const ext = body.exteriorWallThickness;
  const interiorXLo = ext;
  const interiorXHi = body.footprintWidth - ext;
  const interiorZLo = ext;
  const interiorZHi = body.footprintDepth - ext;

  // Index doorways by (ownerRoomId, axis) for O(1) lookup while iterating
  // room partitions.
  const doorIndex = new Map<string, PartitionDoorConfig>();
  for (const door of story.partitionDoors) {
    doorIndex.set(`${door.ownerRoomId}|${door.axis}`, door);
  }

  /** Add a solid axis-aligned slab between two local-footprint coords. */
  const slab = (
    name: string,
    xc: number,
    yc: number,
    zc: number,
    sx: number,
    sy: number,
    sz: number,
  ) => {
    if (sx < 1e-3 || sy < 1e-3 || sz < 1e-3) return;
    const mesh = new Mesh(new BoxGeometry(sx, sy, sz), kit.interior);
    mesh.name = name;
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.position.set(
      -body.footprintWidth / 2 + xc,
      yc,
      -body.footprintDepth / 2 + zc,
    );
    group.add(mesh);
  };

  /**
   * Render a visible interior door inside its frame, wrapped in a pivot
   * group so it can swing on a hinge.
   *
   * - For an 'X' partition (vertical wall, normal along X) the door slab
   *   is thin along X (slabSx=0.04) and spans `doorWidth` along Z. The
   *   hinge edge is one of the Z corners (we pick -Z, the "low Z" end);
   *   the door swings about its local Y so positive degrees push the slab
   *   into +X (one of the two adjacent rooms).
   * - For a 'Z' partition the opposite — slab is thin along Z, spans
   *   `doorWidth` along X, hinge at the -X corner.
   */
  const addInteriorDoorSlab = (
    name: string,
    ownerRoomId: string,
    xc: number,
    zc: number,
    doorWidth: number,
    doorHeight: number,
    axis: 'X' | 'Z',
  ) => {
    // Sit the slab inside a thin jamb (interior door frame). The jamb hugs
    // the opening on top and the two sides; the door slab fills the
    // remaining inner rectangle. Without the jamb the gap between the
    // (slightly-shrunk) slab and the partition wall reads as a black slot
    // — the user reported this in image 1.
    const jambThickness = 0.04;
    const jambDepth = partitionThickness + 0.02;
    const innerWidth = Math.max(0.5, doorWidth - 2 * jambThickness);
    const innerHeight = Math.max(0.6, doorHeight - jambThickness);
    // Slab fills the inner opening flush; we keep a 3mm hinge-side gap so
    // the slab can rotate freely without grinding into the jamb mesh.
    const slabGap = 0.003;
    const slabSize = 0.04;
    const slabSx = axis === 'X' ? slabSize : innerWidth - slabGap;
    const slabSz = axis === 'X' ? innerWidth - slabGap : slabSize;

    // World-space pivot point at the hinge corner of the INNER opening
    // (i.e. just past the jamb on the hinge side).
    let pivotX: number;
    let pivotZ: number;
    if (axis === 'X') {
      pivotX = xc;
      pivotZ = zc - doorWidth / 2 + jambThickness;
    } else {
      pivotX = xc - doorWidth / 2 + jambThickness;
      pivotZ = zc;
    }
    const def: HingeDef = {
      id: `intdoor-L${story.level}-${ownerRoomId}-${axis}`,
      label: `Interior Door L${story.level} · ${ownerRoomId} · ${axis === 'X' ? 'X-wall' : 'Z-wall'}`,
      category: 'interior-door',
      axis: 'Y',
      minDeg: 0,
      maxDeg: 95,
      openSignFromMin: 0,
    };
    hinges.add(def);

    // Wrap the hinge + jamb in a named container so exports read as
    // `InteriorDoor_<id> { Hinge_* { Leaf, Knob }, Jamb_* }`.
    const intDoorGroup = new Group();
    intDoorGroup.name = `InteriorDoor_${def.id}`;
    group.add(intDoorGroup);

    const pivot = new Group();
    pivot.name = `Hinge_${def.id}`;
    pivot.userData.hinge = def;
    pivot.position.set(
      -body.footprintWidth / 2 + pivotX,
      0,
      -body.footprintDepth / 2 + pivotZ,
    );

    const door = new Mesh(
      new BoxGeometry(slabSx, innerHeight - 0.005, slabSz),
      kit.interiorDoor,
    );
    door.name = 'Leaf';
    door.castShadow = true;
    door.receiveShadow = true;
    if (axis === 'X') {
      door.position.set(0, innerHeight / 2, (innerWidth - slabGap) / 2);
    } else {
      door.position.set((innerWidth - slabGap) / 2, innerHeight / 2, 0);
    }
    pivot.add(door);

    // Knob on the latch (free) edge.
    const knobR = 0.03;
    const knob = new Mesh(
      new CylinderGeometry(knobR, knobR, 0.05, 12),
      kit.doorKnob,
    );
    knob.name = 'Knob';
    if (axis === 'X') {
      knob.rotation.z = Math.PI / 2;
      knob.position.set(0, 1.05, innerWidth - 0.08);
    } else {
      knob.rotation.x = Math.PI / 2;
      knob.position.set(innerWidth - 0.08, 1.05, 0);
    }
    knob.castShadow = true;
    knob.receiveShadow = true;
    pivot.add(knob);

    intDoorGroup.add(pivot);

    // Jamb (frame) attached to the partition wall — does NOT swing. It's
    // authored in body-local coords and added to the container so it
    // stays put when the door rotates. Parameter `name` of the caller
    // (e.g. "Partition_{room}_X_door") is discarded — the container
    // name carries the logical identity for exports.
    void name;
    addInteriorDoorJamb(
      intDoorGroup,
      kit,
      body,
      xc,
      zc,
      doorWidth,
      doorHeight,
      jambThickness,
      jambDepth,
      axis,
    );
  };

  /**
   * Build a partition wall along the Z axis at x = xPos, spanning
   * z ∈ [zLo, zHi]. If the doorway lookup has an entry, the wall is split
   * around a doorway sized per the schema; otherwise it's a single slab.
   */
  const addZPartition = (
    name: string,
    ownerRoomId: string,
    xPos: number,
    zLo: number,
    zHi: number,
    door: PartitionDoorConfig | undefined,
  ) => {
    const lo = Math.max(zLo, interiorZLo);
    const hi = Math.min(zHi, interiorZHi);
    const len = hi - lo;
    if (len < 0.1) return;
    if (!door) {
      const zc = (lo + hi) / 2;
      slab(name, xPos, partitionHeight / 2, zc, partitionThickness, partitionHeight, len);
      return;
    }
    // Door center along the FULL owner wall (zLo..zHi); we then clip to
    // the interior-clamped span.
    const fullCenter = zLo + door.tCenter * (zHi - zLo);
    const dW = door.width;
    const dH = Math.min(door.height, partitionHeight - 0.15);
    const doorLo = Math.max(lo, fullCenter - dW / 2);
    const doorHi = Math.min(hi, fullCenter + dW / 2);
    if (doorHi - doorLo < 0.4) {
      slab(name, xPos, partitionHeight / 2, (lo + hi) / 2, partitionThickness, partitionHeight, len);
      return;
    }
    if (doorLo > lo) {
      slab(
        `${name}_a`,
        xPos,
        partitionHeight / 2,
        (lo + doorLo) / 2,
        partitionThickness,
        partitionHeight,
        doorLo - lo,
      );
    }
    if (hi > doorHi) {
      slab(
        `${name}_b`,
        xPos,
        partitionHeight / 2,
        (doorHi + hi) / 2,
        partitionThickness,
        partitionHeight,
        hi - doorHi,
      );
    }
    slab(
      `${name}_lintel`,
      xPos,
      dH + (partitionHeight - dH) / 2,
      (doorLo + doorHi) / 2,
      partitionThickness,
      partitionHeight - dH,
      doorHi - doorLo,
    );
    addInteriorDoorSlab(`${name}_door`, ownerRoomId, xPos, (doorLo + doorHi) / 2, doorHi - doorLo, dH, 'X');
  };

  const addXPartition = (
    name: string,
    ownerRoomId: string,
    zPos: number,
    xLo: number,
    xHi: number,
    door: PartitionDoorConfig | undefined,
  ) => {
    const lo = Math.max(xLo, interiorXLo);
    const hi = Math.min(xHi, interiorXHi);
    const len = hi - lo;
    if (len < 0.1) return;
    if (!door) {
      const xc = (lo + hi) / 2;
      slab(name, xc, partitionHeight / 2, zPos, len, partitionHeight, partitionThickness);
      return;
    }
    const fullCenter = xLo + door.tCenter * (xHi - xLo);
    const dW = door.width;
    const dH = Math.min(door.height, partitionHeight - 0.15);
    const doorLo = Math.max(lo, fullCenter - dW / 2);
    const doorHi = Math.min(hi, fullCenter + dW / 2);
    if (doorHi - doorLo < 0.4) {
      slab(name, (lo + hi) / 2, partitionHeight / 2, zPos, len, partitionHeight, partitionThickness);
      return;
    }
    if (doorLo > lo) {
      slab(
        `${name}_a`,
        (lo + doorLo) / 2,
        partitionHeight / 2,
        zPos,
        doorLo - lo,
        partitionHeight,
        partitionThickness,
      );
    }
    if (hi > doorHi) {
      slab(
        `${name}_b`,
        (doorHi + hi) / 2,
        partitionHeight / 2,
        zPos,
        hi - doorHi,
        partitionHeight,
        partitionThickness,
      );
    }
    slab(
      `${name}_lintel`,
      (doorLo + doorHi) / 2,
      dH + (partitionHeight - dH) / 2,
      zPos,
      doorHi - doorLo,
      partitionHeight - dH,
      partitionThickness,
    );
    addInteriorDoorSlab(`${name}_door`, ownerRoomId, (doorLo + doorHi) / 2, zPos, doorHi - doorLo, dH, 'Z');
  };

  for (const room of story.rooms) {
    const rxEdge = room.x + room.width;
    const rzEdge = room.z + room.depth;
    if (rxEdge < body.footprintWidth - 1e-3) {
      addZPartition(
        `Partition_${room.id}_X`,
        room.id,
        rxEdge,
        room.z,
        room.z + room.depth,
        doorIndex.get(`${room.id}|X`),
      );
    }
    if (rzEdge < body.footprintDepth - 1e-3) {
      addXPartition(
        `Partition_${room.id}_Z`,
        room.id,
        rzEdge,
        room.x,
        room.x + room.width,
        doorIndex.get(`${room.id}|Z`),
      );
    }
  }

  return group;
}
